import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import SubscriptionPlans, {
  SubscriptionPlansHandle,
} from '../../components/SubscriptionPlans';
import { authApiClient } from '../../authApiClient';
import { hostApiClient } from '../../hostApiClient';
import { authService } from '../../services/AuthService';
import { Organization, OrganizationType } from '../../models/Organization';
import {
  OrganizationSubscriptionStatus,
  SubscriptionStatus,
} from '../../models/Subscription';
import {
  beginSubscriptionOnboardingSession,
  cacheOrganizationType,
  clearCachedOrganizationType,
  clearOnboardingDisplayName,
  clearSubscriptionOnboardingActive,
  isSubscriptionOnboardingActive,
  markSubscriptionOnboardingActive,
  readOnboardingAccessToken,
  readOnboardingDisplayName,
  resolveOnboardingContinuePath,
} from '../../utils/subscriptionOnboarding';
import { getCachedProfileDisplay } from '../../services/profileDisplayCache';
import { userApiClient } from '../../userApiClient';
import {
  clearSubscriptionAwaitingStripe,
  hasSubscriptionWebhookCaughtUp,
  notifyOrganizationTypeUpdated,
  readSubscriptionAwaitingStripe,
  STRIPE_RETURN_POLL_INTERVAL_MS,
  STRIPE_RETURN_POLL_TIMEOUT_MS,
} from '../../utils/subscriptionStripeReturn';

/**
 * Fullscreen plan picker during signup — outside the host dashboard shell.
 * Catalogue is public; org is created only when a plan is chosen.
 */
const SubscriptionOnboardingPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const continuePath = resolveOnboardingContinuePath(searchParams.get('next'));
  const finishRef = useRef(false);
  const plansRef = useRef<SubscriptionPlansHandle>(null);

  const [musicianLoading, setMusicianLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [subscription, setSubscription] = useState<OrganizationSubscriptionStatus | null>(null);
  const [syncingAfterStripe, setSyncingAfterStripe] = useState(
    () => Boolean(readSubscriptionAwaitingStripe()),
  );
  const [stripeSyncTimedOut, setStripeSyncTimedOut] = useState(false);
  const isHost = authService.getRoles().includes('HOST');

  useEffect(() => {
    let cancelled = false;

    const bootstrapExistingProfile = async () => {
      markSubscriptionOnboardingActive(searchParams.get('next'));
      if (readOnboardingDisplayName()) {
        return;
      }

      const accessToken = authService.getAccessToken();
      if (!accessToken) {
        return;
      }

      try {
        const cachedName = getCachedProfileDisplay()?.name?.trim();
        let displayName = cachedName;
        if (!displayName) {
          const roles = authService.getRoles();
          const profile = roles.includes('HOST')
            ? await hostApiClient.getProfile()
            : await userApiClient.getProfile();
          if (cancelled) return;
          displayName = profile.name?.trim();
        }
        if (displayName) {
          beginSubscriptionOnboardingSession({
            accessToken,
            displayName,
            next: searchParams.get('next'),
          });
        }
      } catch (err) {
        console.error('Failed to prefill subscription onboarding from profile:', err);
      }
    };

    void bootstrapExistingProfile();
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const finishOnboarding = useCallback(
    (orgType?: OrganizationType | null) => {
      if (finishRef.current) return;
      finishRef.current = true;
      clearOnboardingDisplayName();
      clearSubscriptionOnboardingActive();
      if (orgType) {
        cacheOrganizationType(orgType);
        notifyOrganizationTypeUpdated(orgType);
      } else if (organization?.organizationType) {
        cacheOrganizationType(organization.organizationType);
        notifyOrganizationTypeUpdated(organization.organizationType);
      }
      navigate(continuePath, { replace: true });
    },
    [continuePath, navigate, organization?.organizationType],
  );

  const continueAsMusician = useCallback(async () => {
    setMusicianLoading(true);
    setError(null);
    try {
      // Existing musicians already completed profile — skip re-complete.
      if (authService.getRoles().includes('USER')) {
        clearOnboardingDisplayName();
        clearSubscriptionOnboardingActive();
        clearCachedOrganizationType();
        navigate('/user/search', { replace: true });
        return;
      }

      const accessToken = readOnboardingAccessToken();
      const displayName = readOnboardingDisplayName();
      if (!accessToken || !displayName) {
        setError(
          t(
            'subscription.onboarding.musicianMissingSession',
            'Could not finish musician signup. Please sign in again.',
          ),
        );
        return;
      }

      await authApiClient.completeUserProfile({
        accessToken,
        name: displayName,
      });
      const refreshed = await authService.refreshSessionAfterProfileCompletion();
      if (!refreshed) {
        setError(
          t(
            'authOnboarding.errors.sessionRefreshFailed',
            'Could not update your session. Please sign in again.',
          ),
        );
        return;
      }

      clearOnboardingDisplayName();
      clearSubscriptionOnboardingActive();
      clearCachedOrganizationType();
      navigate('/user/search', { replace: true });
    } catch (err) {
      console.error('Continue as musician failed:', err);
      setError(
        t(
          'subscription.onboarding.musicianCompleteFailed',
          'Could not continue as a musician. Please try again.',
        ),
      );
    } finally {
      setMusicianLoading(false);
    }
  }, [navigate, t]);

  const refreshBillingState = useCallback(async () => {
    if (!authService.getRoles().includes('HOST')) {
      return null;
    }
    try {
      setError(null);
      const [org, status] = await Promise.all([
        hostApiClient.getOrganization(),
        hostApiClient.getSubscription(),
      ]);
      setOrganization(org);
      setSubscription(status);
      return { org, status };
    } catch (err) {
      // Pre-plan signup has no host org yet — ignore.
      console.error('Error fetching subscription state during onboarding:', err);
      return null;
    }
  }, []);

  useEffect(() => {
    if (isHost || syncingAfterStripe) {
      void refreshBillingState();
    }
  }, [isHost, refreshBillingState, syncingAfterStripe]);

  useEffect(() => {
    const baseline = readSubscriptionAwaitingStripe();
    if (!baseline) {
      setSyncingAfterStripe(false);
      return;
    }

    setSyncingAfterStripe(true);
    setStripeSyncTimedOut(false);
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;

    const finish = (timedOut: boolean, org?: Organization) => {
      clearSubscriptionAwaitingStripe();
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = undefined;
      }
      if (!cancelled) {
        setSyncingAfterStripe(false);
        if (timedOut) {
          setStripeSyncTimedOut(true);
        } else {
          finishOnboarding(org?.organizationType);
        }
      }
    };

    const pollOnce = async () => {
      if (Date.now() - baseline.startedAt > STRIPE_RETURN_POLL_TIMEOUT_MS) {
        finish(true);
        return;
      }
      try {
        const [org, status] = await Promise.all([
          hostApiClient.getOrganization(),
          hostApiClient.getSubscription(),
        ]);
        if (cancelled) return;
        setOrganization(org);
        setSubscription(status);
        if (
          hasSubscriptionWebhookCaughtUp(baseline, {
            planCode: status.planCode,
            status: status.status,
            organizationType: org.organizationType,
          })
        ) {
          finish(false, org);
        }
      } catch (err) {
        console.error('Error polling subscription after Stripe return:', err);
      }
    };

    void pollOnce();
    intervalId = setInterval(() => {
      void pollOnce();
    }, STRIPE_RETURN_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [finishOnboarding]);

  const status = subscription?.status ?? null;
  const planCode = subscription?.planCode ?? null;
  const hasActivatedSubscription = Boolean(
    status &&
      planCode &&
      (status === SubscriptionStatus.ACTIVE || status === SubscriptionStatus.TRIALING),
  );

  useEffect(() => {
    if (!syncingAfterStripe && hasActivatedSubscription) {
      finishOnboarding(organization?.organizationType);
    }
  }, [
    finishOnboarding,
    hasActivatedSubscription,
    organization?.organizationType,
    syncingAfterStripe,
  ]);

  useEffect(() => {
    if (!isSubscriptionOnboardingActive()) {
      markSubscriptionOnboardingActive(searchParams.get('next'));
    }
  }, [searchParams]);

  const pageTitle = t('subscription.onboarding.pageTitle', 'Almost there — pick a plan');

  return (
    <>
      <SEO title={pageTitle} noindex={true} />
      <Container component="main" maxWidth="lg">
        <Box sx={{ mt: { xs: 3, sm: 5 }, mb: 6 }}>
          <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', md: 'flex-start' }}
              justifyContent="space-between"
              sx={{ mb: 2.5 }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1.25,
                  }}
                >
                  <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
                    {pageTitle}
                  </Typography>
                  <Chip
                    color="success"
                    size="small"
                    label={t('subscription.onboarding.trialBadge', 'Free Trial')}
                    sx={{ fontWeight: 700 }}
                  />
                </Box>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 640 }}>
                  {t(
                    'subscription.onboarding.pageSubtitle',
                    'Pick a plan. Full access for 30 days — you will not be charged until the trial ends. Cancel anytime.',
                  )}
                </Typography>
              </Box>
              <Button
                variant="text"
                onClick={() => void continueAsMusician()}
                disabled={musicianLoading}
                sx={{
                  fontWeight: 600,
                  whiteSpace: { sm: 'nowrap' },
                  flexShrink: 0,
                  alignSelf: { xs: 'flex-end', md: 'flex-start' },
                }}
              >
                {musicianLoading
                  ? t('common.loading', 'Loading...')
                  : t('subscription.onboarding.continueAsMusician', 'Continue as a musician')}
              </Button>
            </Stack>

            {syncingAfterStripe && (
              <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 2.5 }}>
                {t(
                  'subscription.syncingAfterStripe',
                  'Checking whether Stripe confirmed your plan change…',
                )}
              </Alert>
            )}

            {stripeSyncTimedOut && !syncingAfterStripe && (
              <Alert
                severity="warning"
                sx={{ mb: 2.5 }}
                onClose={() => setStripeSyncTimedOut(false)}
              >
                {t(
                  'subscription.stripeSyncTimedOut',
                  'No plan change showed up yet. If you confirmed in Stripe, wait a moment and refresh — local webhooks may still be catching up. If you cancelled, your current plan is unchanged.',
                )}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <SubscriptionPlans
              ref={plansRef}
              organizationType={null}
              currentPlanCode={planCode}
              currentStatus={status}
              onboarding
              onSubscriptionChange={() => {
                void refreshBillingState();
              }}
            />

            <Box
              sx={{
                mt: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Button
                size="small"
                variant="text"
                onClick={() =>
                  navigate(
                    authService.getRoles().includes('USER') || authService.getRoles().includes('TUTOR')
                      ? '/pricing'
                      : '/complete-profile',
                    { replace: true },
                  )
                }
                sx={{ fontWeight: 600, ml: -0.5 }}
              >
                {t('subscription.onboarding.goBack', 'Go back')}
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={() => plansRef.current?.selectPopularPlan()}
                sx={{ fontWeight: 600, mr: -0.5 }}
              >
                {t('common.next', 'Next')}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default SubscriptionOnboardingPage;
